import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles, Loader2, Percent, ArrowRight, BarChart3, Map as MapIcon, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HIGHLIGHT = "membership";
const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [cycle, setCycle] = useState(params.get("cycle") === "yearly" ? "yearly" : "monthly");
  const [welcome, setWelcome] = useState(null); // { tier } after successful checkout
  const [agreed, setAgreed] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    try { const r = await api.get("/billing/status"); setStatus(r.data); } catch {}
  }, [user]);

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const poll = useCallback(async (sid, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Still processing — check back shortly."); return; }
    try {
      const r = await api.get(`/billing/checkout/status/${sid}`);
      if (r.data.payment_status === "paid") {
        setVerifying(false); setWelcome({ tier: r.data.tier || "" });
        setParams({}, { replace: true }); loadStatus(); return;
      }
      if (r.data.status === "expired") { setVerifying(false); toast.error("Checkout expired."); setParams({}, { replace: true }); return; }
      setTimeout(() => poll(sid, attempt + 1), 2000);
    } catch { setTimeout(() => poll(sid, attempt + 1), 2000); }
  }, [setParams, loadStatus]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setVerifying(true); poll(sid); }
  }, [params, poll]);

  const subscribe = async (planId) => {
    if (!user) { toast.error("Sign in to subscribe"); navigate("/login"); return; }
    setBusy(planId);
    try {
      const body = { plan: planId, origin_url: window.location.origin, billing_cycle: cycle };
      const r = await api.post("/billing/checkout", body);
      window.location.href = r.data.url;
    } catch { toast.error("Could not start checkout"); setBusy(""); }
  };

  const currentTier = status?.tier;
  const memPlan = plans.find((p) => p.id !== "free") || null;
  const yearlyPrice = memPlan?.prices?.yearly;
  const monthlyPrice = memPlan?.prices?.monthly;
  const annualSavings = (monthlyPrice && yearlyPrice) ? Math.round(monthlyPrice * 12 - yearlyPrice) : null;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-accent mb-4">Membership</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium">One membership. Everything unlocked.</h1>
        <p className="text-muted-foreground mt-4 text-base">Full access to every home, vehicle, valuation and Market Intelligence tool on Vaelith. Auto-renews — cancel anytime.</p>
      </div>

      <div className="flex items-center justify-center mt-8">
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface border border-border" data-testid="billing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
            className={`h-9 px-5 rounded-full text-sm transition-colors ${cycle === "monthly" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
          <button onClick={() => setCycle("yearly")} data-testid="cycle-yearly"
            className={`h-9 px-5 rounded-full text-sm transition-colors flex items-center gap-2 ${cycle === "yearly" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            Yearly<span className="font-mono-data text-[10px] uppercase tracking-wider text-accent">Save 20%</span>
          </button>
        </div>
      </div>

      {!status?.active && (cycle === "monthly" ? (
        <div className="flex justify-center mt-5">
          <button onClick={() => setCycle("yearly")} data-testid="annual-nudge-banner"
            className="group inline-flex items-center gap-2.5 rounded-full border border-accent/50 bg-accent/10 px-5 py-2.5 text-sm text-accent hover:bg-accent/20 transition-colors shadow-[0_0_28px_-8px_var(--accent)]">
            <Percent className="w-4 h-4 shrink-0" />
            <span className="font-medium">Switch to annual &amp; save {annualSavings ? `$${fmt(annualSavings)}` : "20%"} a year</span>
            {yearlyPrice ? <span className="font-mono-data text-[11px] uppercase tracking-wider opacity-80 hidden sm:inline">${fmt(yearlyPrice)}/yr</span> : null}
            <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      ) : (
        <div className="flex justify-center mt-5">
          <span data-testid="annual-savings-confirm"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-5 py-2.5 text-sm text-emerald-500">
            <Check className="w-4 h-4 shrink-0" /> You're saving {annualSavings ? `$${fmt(annualSavings)}` : "20%"} a year with annual billing
          </span>
        </div>
      ))}

      {verifying && (
        <div className="flex items-center justify-center gap-2 mt-8 font-mono-data text-sm text-muted-foreground" data-testid="verifying-payment">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
        </div>
      )}
      {status?.is_admin && <div className="text-center mt-6 font-mono-data text-xs uppercase tracking-wider text-emerald-500" data-testid="admin-note">Admin account — full access enabled</div>}

      <div className="mt-14 max-w-md mx-auto">
        {plans.filter((p) => p.id !== "agent_dealer").map((p, i) => {
          const isCurrent = currentTier === p.tier && !status?.is_admin;
          const highlight = p.id === HIGHLIGHT;
          const yearly = cycle === "yearly";
          const amount = yearly ? p.prices.yearly : p.prices.monthly;
          const perMonth = yearly ? Math.round(p.prices.yearly / 12) : p.prices.monthly;
          return (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className={`relative rounded-2xl p-8 flex flex-col ${highlight ? "bg-foreground text-background" : "bg-surface border border-border"}`}
              data-testid={`plan-${p.id}`}>
              <div className={`font-mono-data text-[10px] uppercase tracking-[0.2em] ${highlight ? "text-background/60" : "text-muted-foreground"}`}>{p.audience}</div>
              <h3 className="font-display text-2xl font-medium mt-2">{p.name}</h3>
              <div className="flex items-baseline gap-1 mt-4">
                <span className="font-mono-data text-4xl" data-testid={`price-${p.id}`}>${fmt(amount)}</span>
                <span className={`font-mono-data text-sm ${highlight ? "text-background/60" : "text-muted-foreground"}`}>{yearly ? "/yr" : "/mo"}</span>
              </div>
              <div className={`font-mono-data text-[11px] mt-1 ${highlight ? "text-background/60" : "text-muted-foreground"}`}>
                {yearly ? `${fmt(perMonth)}/mo billed annually · save 20%` : "Billed monthly · auto-renews"}
              </div>
              {!yearly && !isCurrent && (
                <button onClick={() => setCycle("yearly")} data-testid={`card-annual-nudge-${p.id}`}
                  className={`mt-3 inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider transition-colors ${highlight ? "bg-accent/20 text-accent hover:bg-accent/30" : "border border-accent/40 text-accent hover:bg-accent/10"}`}>
                  <Percent className="w-3 h-3" /> Save 20% with annual{p.prices?.yearly ? ` · $${fmt(p.prices.yearly)}/yr` : ""}
                </button>
              )}

              <ul className="space-y-3 mt-6 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-accent" : "text-accent"}`} />
                    <span className={highlight ? "text-background/90" : ""}>{f}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <Button disabled data-testid={`current-${p.id}`} className="rounded-full mt-8 w-full" variant={highlight ? "secondary" : "outline"}>Current plan</Button>
              ) : (
                <AlertDialog onOpenChange={(o) => { if (o) setAgreed(false); }}>
                  <AlertDialogTrigger asChild>
                    <Button disabled={busy === p.id} data-testid={`subscribe-${p.id}`}
                      className={`rounded-full mt-8 w-full ${highlight ? "bg-background text-foreground hover:bg-background/90" : ""}`}>
                      {busy === p.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</> : <><Sparkles className="w-4 h-4 mr-2" />Subscribe</>}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-md" data-testid={`plan-dialog-${p.id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl">{p.name}</AlertDialogTitle>
                      <AlertDialogDescription>
                        <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent">Marketed towards</span>
                        <span className="block mt-1 text-foreground text-sm">{p.audience}.</span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="rounded-xl border border-border bg-surface px-4 py-3 flex items-baseline justify-between">
                      <span className="font-display text-2xl">${fmt(amount)}<span className="font-mono-data text-xs text-muted-foreground ml-1">{yearly ? "/yr" : "/mo"}</span></span>
                      <span className="font-mono-data text-[11px] uppercase tracking-wider text-muted-foreground">{yearly ? `${fmt(perMonth)}/mo · save 20%` : "Auto-renews monthly"}</span>
                    </div>
                    <div className="text-sm">
                      <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">What you'll unlock</div>
                      <ul className="space-y-2 max-h-56 overflow-auto pr-1">
                        {p.features.map((f, j) => (
                          <li key={j} className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 shrink-0 text-accent" /><span>{f}</span></li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground">This is an auto-renewing subscription billed {yearly ? "annually" : "monthly"} via Stripe. You can cancel anytime from your dashboard and keep access until the period ends.</p>
                    <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer select-none rounded-lg border border-border bg-surface px-3 py-2.5" data-testid={`plan-agree-label-${p.id}`}>
                      <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid={`plan-agree-checkbox-${p.id}`}
                        className="h-4 w-4 mt-0.5 rounded border-border accent-accent cursor-pointer shrink-0" />
                      <span>I agree to the{" "}
                        <a href="/legal/subscription-agreement" target="_blank" rel="noreferrer" className="text-accent hover:underline">Subscription Agreement</a>,{" "}
                        <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-accent hover:underline">Terms</a> &amp;{" "}
                        <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-accent hover:underline">Privacy Policy</a>, and authorize Vaelith to charge ${fmt(amount)} {yearly ? "today and each year" : "today and each month"} until I cancel.
                      </span>
                    </label>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`plan-dialog-cancel-${p.id}`}>Not now</AlertDialogCancel>
                      <AlertDialogAction disabled={!agreed} data-testid={`plan-dialog-confirm-${p.id}`} onClick={() => subscribe(p.id)}>Continue to payment</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="text-center text-xs text-muted-foreground/70 mt-10">Payments are processed securely by Stripe. Subscriptions renew automatically — cancel anytime from your dashboard.</p>

      <Dialog open={!!welcome} onOpenChange={(o) => { if (!o) setWelcome(null); }}>
        <DialogContent className="max-w-lg" data-testid="member-welcome-modal">
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center mb-1"><PartyPopper className="w-6 h-6 text-accent" /></div>
            <DialogTitle className="font-display text-3xl">Welcome to Vaelith</DialogTitle>
            <DialogDescription>Your membership is active — the entire platform is unlocked. Two great places to start:</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <button onClick={() => { setWelcome(null); navigate("/market"); }} data-testid="welcome-goto-market"
              className="text-left rounded-xl border border-border bg-surface p-4 hover:border-accent transition-colors group">
              <BarChart3 className="w-6 h-6 text-accent" />
              <div className="font-display text-lg mt-2">Market Intelligence</div>
              <p className="text-sm text-muted-foreground mt-1">Live dashboards, medians, pulse & value tiers across every tracked market.</p>
              <span className="inline-flex items-center gap-1 mt-2 font-mono-data text-[11px] uppercase tracking-wider text-accent">Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </button>
            <button onClick={() => { setWelcome(null); navigate("/coverage"); }} data-testid="welcome-goto-coverage"
              className="text-left rounded-xl border border-border bg-surface p-4 hover:border-accent transition-colors group">
              <MapIcon className="w-6 h-6 text-accent" />
              <div className="font-display text-lg mt-2">Coverage Map</div>
              <p className="text-sm text-muted-foreground mt-1">Explore ZIP-level heatmaps, pulse movers & compare areas on the interactive map.</p>
              <span className="inline-flex items-center gap-1 mt-2 font-mono-data text-[11px] uppercase tracking-wider text-accent">Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </button>
          </div>
          <Button variant="ghost" className="rounded-full mt-1 w-full" data-testid="welcome-dismiss" onClick={() => { setWelcome(null); navigate("/dashboard"); }}>Take me to my dashboard</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
